import { motion } from "framer-motion";
import { ArrowRight, AlertTriangle } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_menaam-toekomst/artifacts/ecxvwt22_Menaam_fan_%27e_Slappeterpster_kant_%C3%B4f2.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Het dorp Menaam met kerktoren onder een dramatische lucht"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2E22] via-[#1A2E22]/90 to-[#1A2E22]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E22] via-transparent to-[#1A2E22]/30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-16">
        <div className="max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block text-xs font-bold tracking-[0.18em] uppercase mb-6 px-3.5 py-1.5 rounded-full bg-[#74C69D]/15 text-[#74C69D]"
          >
            Menaam · Gemeente Waadhoeke
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-[#FAF7F2] font-black leading-[1.02] tracking-tight text-5xl sm:text-6xl lg:text-7xl"
          >
            Er staat een{" "}
            <span className="text-[#74C69D]">nieuwe school</span>{" "}
            in de planning.<br />
            En dan?
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="mt-7 text-lg leading-relaxed text-[#FAF7F2]/75 max-w-xl"
          >
            De nieuwbouw van de school in Menaam staat voor de deur. Dat biedt een
            unieke kans voor het hele dorp. Maar bouwen we alleen een school of
            pakken we de kans waar Menaam al decennia over droomt?
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.28 }}
            className="mt-5 font-heading italic text-xl sm:text-2xl text-[#74C69D]"
          >
            Mei elkoar, foar elkoar.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.34 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <a
              href="#situatie"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 font-bold text-sm px-7 py-3.5 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
            >
              Lees de situatie
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#meedoen"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center font-medium text-sm px-7 py-3.5 rounded-full border border-[#FAF7F2]/30 text-[#FAF7F2]/90 hover:bg-[#FAF7F2]/10 transition-colors"
            >
              Jouw inbreng
            </a>
          </motion.div>

          {/* Warning / disclaimer banner */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.46 }}
            data-testid="hero-disclaimer"
            className="mt-12 flex gap-4 items-start rounded-xl border border-[#F4A261]/40 bg-[#F4A261]/10 px-6 py-5 backdrop-blur-sm max-w-xl"
          >
            <AlertTriangle size={22} className="text-[#F4A261] shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-[#FAF7F2]/75 m-0">
              <strong className="text-[#F4A261]">Let op:</strong> Alleen de nieuwbouw
              van de school staat vast. De locatie, de invulling náást de school en de
              financiering zijn nog{" "}
              <strong className="text-[#F4A261]">geen besloten zaken</strong>. De
              werkgroep en gemeente verkennen dit samen. Jouw mening telt mee.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
